import moment from 'moment';

import {
  Commission,
  CommissionStatusEnum,
  Referral,
  ReferralCommissionEntry,
  ReferralCommissionStatusEnum,
  ReferralPayoutStatusEnum,
  ReferralReferredUser,
  ReferralStatusEnum,
} from '../../lib/models/crm/types';
import { NullableNumber, NullableString } from '../../lib/util/types';

import {
  EMPTY_AMOUNT_PLACEHOLDER,
  GROUP_DATE_FORMAT,
  GROUP_DATE_KEY_FORMAT,
  PAYOUT_REQUEST_ID_PREFIX,
  REFERRAL_LINK_PROTOCOL,
} from './Referrals.constants';
import { CommissionGroup, ReferralGroup, SocialNetworkEnum } from './Referrals.types';

/** Rows are grouped by day, so the timestamps collapse to a date key. */
const toGroupKey = (utcDate: string): string => moment(utcDate).format(GROUP_DATE_KEY_FORMAT);

const toPayoutRequestId = (payoutRequestId: NullableNumber): NullableString => (
  payoutRequestId == null ? null : `${PAYOUT_REQUEST_ID_PREFIX}${payoutRequestId}`
);

/** A payout request that ended badly, whichever side ended it. */
const REJECTED_PAYOUT_STATUSES: readonly ReferralPayoutStatusEnum[] = [
  ReferralPayoutStatusEnum.rejected,
  ReferralPayoutStatusEnum.cancelled,
];

const toCommissionStatus = (
  status: ReferralCommissionStatusEnum,
  payoutStatus: ReferralPayoutStatusEnum,
): CommissionStatusEnum => {
  if (REJECTED_PAYOUT_STATUSES.includes(payoutStatus)) {
    return CommissionStatusEnum.rejected;
  }

  switch (status) {
    case ReferralCommissionStatusEnum.requested:
      return CommissionStatusEnum.requested;
    case ReferralCommissionStatusEnum.paid:
      return CommissionStatusEnum.paid;
    // A cancelled commission is presented as rejected - the UI draws no distinction.
    case ReferralCommissionStatusEnum.rejected:
    case ReferralCommissionStatusEnum.cancelled:
      return CommissionStatusEnum.rejected;
    default:
      return CommissionStatusEnum.available;
  }
};

export const toReferralRows = (referredUsers: ReferralReferredUser[]): Referral[] => (
  referredUsers.map((user) => ({
    id: user.internal_reference ?? user.registered_at_utc,
    maskedEmail: user.masked_email ?? EMPTY_AMOUNT_PLACEHOLDER,
    registeredAt: toGroupKey(user.registered_at_utc),
    commission: user.commission_amount,
    status: user.purchase_made ? ReferralStatusEnum.purchased : ReferralStatusEnum.notPurchased,
  }))
);

export const toCommissionRows = (commissions: ReferralCommissionEntry[]): Commission[] => (
  commissions.map((commission) => ({
    id: `${commission.id}`,
    maskedEmail: commission.masked_referred_email ?? EMPTY_AMOUNT_PLACEHOLDER,
    requestId: toPayoutRequestId(commission.payout_request_id),
    commission: commission.commission_amount,
    status: toCommissionStatus(commission.status, commission.payout_status),
    statusChangedAt: toGroupKey(commission.status_changed_at_utc),
  }))
);

export const formatReferralDate = (isoDate: string): string => moment(isoDate).format(GROUP_DATE_FORMAT);

export const groupReferralsByRegistration = (referrals: Referral[]): ReferralGroup[] => (
  referrals.reduce<ReferralGroup[]>((groups, referral) => {
    const lastGroup = groups[groups.length - 1];

    if (lastGroup && lastGroup.registeredAt === referral.registeredAt) {
      lastGroup.items.push(referral);

      return groups;
    }

    return [...groups, { registeredAt: referral.registeredAt, items: [referral] }];
  }, [])
);

export const groupCommissionsByStatusChange = (commissions: Commission[]): CommissionGroup[] => (
  commissions.reduce<CommissionGroup[]>((groups, commission) => {
    const lastGroup = groups[groups.length - 1];
    const belongsToLastGroup = lastGroup
      && lastGroup.statusChangedAt === commission.statusChangedAt
      && lastGroup.status === commission.status;

    if (belongsToLastGroup) {
      lastGroup.items.push(commission);

      return groups;
    }

    return [
      ...groups,
      { statusChangedAt: commission.statusChangedAt, status: commission.status, items: [commission] },
    ];
  }, [])
);

export const buildReferralShareUrl = (link: string): string => (
  /^https?:\/\//.test(link) ? link : `${REFERRAL_LINK_PROTOCOL}${link}`
);

export const buildSocialShareUrl = (network: SocialNetworkEnum, link: string): string => {
  const shareUrl = encodeURIComponent(buildReferralShareUrl(link));

  switch (network) {
    case SocialNetworkEnum.facebook:
      return `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`;
    case SocialNetworkEnum.x:
      return `https://x.com/intent/tweet?url=${shareUrl}`;
    case SocialNetworkEnum.linkedin:
      return `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`;
    case SocialNetworkEnum.whatsapp:
      return `https://wa.me/?text=${shareUrl}`;
    default:
      return buildReferralShareUrl(link);
  }
};
